// src/components/KnowledgeBase.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import knowledgeBaseService from '../services/knowledgeBaseService';
import DocumentList from './DocumentList';
import { 
  Search, FolderOpen, FileText, Download, Eye, Calendar, Upload, 
  X, Tag, Filter, MoreVertical, AlertCircle, CheckCircle, 
  Clock, Loader, Trash2, Edit, Star, Share
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import UploadProgress from './UploadProgress';

declare global {
  interface Window {
    currentDocumentsRequest: number | boolean;
  }
}

// 📋 INTERFACES
interface Category {
  id: string;
  name: string;
  description: string;
  document_count: number;
  icon?: string;
  color?: string;
}

interface DocumentTag {
  id: string;
  name: string;
  color: string;
  auto_assigned: boolean;
}

interface Document {
  id: string;
  title: string;
  description: string;
  category_name: string;
  category_color: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_size_formatted: string;
  processing_status: 'pending' | 'processing' | 'completed' | 'error';
  confidence_score: number;
  view_count: number;
  download_count: number;
  created_at: string;
  updated_at: string;
  tags: string[];
}

interface UploadProgress {
  file: File;
  progress: number;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  documentId?: string;
  error?: string;
}

interface SearchFilters {
  category_id?: string;
  file_type?: string;
  date_range?: string;
  min_confidence?: number;
}

interface KnowledgeBaseProps {
  currentUser?: {
    id: string | number;
    name: string;
    email: string;
  } | null;
}


// 🎨 COMPONENTE TOAST MODERNO
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  isVisible: boolean;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, isVisible, onClose }) => {
  const { isDark } = useTheme();
  
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 4000); // 4 segundos
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;
    
  const getToastStyles = () => {
    const baseStyle = `backdrop-blur-sm border shadow-lg`;
    switch (type) {
      case 'success':
        return `${baseStyle} ${isDark ? 'bg-green-900/90 text-green-100 border-green-700' : 'bg-green-500 text-white border-green-600'}`;
      case 'error':
        return `${baseStyle} ${isDark ? 'bg-red-900/90 text-red-100 border-red-700' : 'bg-red-500 text-white border-red-600'}`;
      case 'warning':
        return `${baseStyle} ${isDark ? 'bg-yellow-900/90 text-yellow-100 border-yellow-700' : 'bg-yellow-500 text-white border-yellow-600'}`;
      default:
        return `${baseStyle} ${isDark ? 'bg-blue-900/90 text-blue-100 border-blue-700' : 'bg-blue-500 text-white border-blue-600'}`;
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      case 'error':
        return <AlertCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in">
      <div className={`${getToastStyles()} px-4 py-3 rounded-xl flex items-center gap-3 min-w-80 max-w-96`}>
        {getIcon()}
        <span className="flex-1 text-sm font-medium">{message}</span>
        <button
          onClick={onClose}
          className="text-current hover:opacity-70 transition-opacity text-lg leading-none"
        >
          ×
        </button>
      </div>
    </div>
  );
};

// 🎨 MODAL DE CONFIRMACIÓN PERSONALIZADO
interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Aceptar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  type = 'danger'
}) => {
  const { isDark } = useTheme();

  if (!isOpen) return null;

  const getButtonStyles = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-500 hover:bg-red-600 text-white';
      case 'warning':
        return 'bg-yellow-500 hover:bg-yellow-600 text-white';
      default:
        return 'bg-blue-500 hover:bg-blue-600 text-white';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in`}>
        <div className={`p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {title}
          </h3>
        </div>
        
        <div className="p-6">
          <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            {message}
          </p>
        </div>
        
        <div className={`p-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-end gap-3`}>
          <button
            onClick={onCancel}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              isDark 
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-lg transition-colors ${getButtonStyles()}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};


const KnowledgeBase: React.FC<KnowledgeBaseProps> = ({ 
  currentUser = null // ✅ Valor por defecto
}) => {
  const { isDark } = useTheme();
  
  // ✅ DEFINIR USUARIO CON VALOR POR DEFECTO
   const user = currentUser || (() => {
    try {
      const savedUser = localStorage.getItem('currentUser');
      return savedUser ? JSON.parse(savedUser) : {
        id: '1',
        name: 'Usuario Temporal',
        email: 'temp@example.com'
      };
    } catch {
      return {
        id: '1',
        name: 'Usuario Temporal', 
        email: 'temp@example.com'
      };
    }
  })();
  
 
  // ✅ NUEVOS ESTADOS PARA TOAST Y MODAL
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [toastVisible, setToastVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef(null);
  const uploadInProgressRef = useRef(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'danger' as 'danger' | 'warning' | 'info'
  });
  
  // ✅ FUNCIÓN PARA MOSTRAR TOAST
  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };
  
  // ✅ FUNCIÓN PARA MOSTRAR MODAL DE CONFIRMACIÓN
  const showConfirm = (title: string, message: string, onConfirm: () => void, type: 'danger' | 'warning' | 'info' = 'danger') => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        onConfirm();
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
      type
    });
  };
  
  // Dentro del componente, después de los estados existentes
const [showUploadProgress, setShowUploadProgress] = useState(false);
const [currentUploadStep, setCurrentUploadStep] = useState(0);
const [uploadProgressPercentage, setUploadProgressPercentage] = useState(0);
const [currentUploadFile, setCurrentUploadFile] = useState<string>('');
const [uploadError, setUploadError] = useState<string>('');
const [isUploading, setIsUploading] = useState(false);
  // 🔄 ESTADOS
  const [categories, setCategories] = useState<Category[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  });

// 📱 CARGAR DATOS INICIALES (solo al montar)
useEffect(() => {
  console.log('🚀 Componente montado - cargando datos iniciales');
  loadCategories();
  loadDocuments();
}, []); // ✅ Sin dependencias circulares

// 🔄 RECARGAR cuando cambian filtros (con debounce para búsqueda)
useEffect(() => {
  const timeoutId = setTimeout(() => {
    console.log('🔄 Filtros cambiaron - recargando documentos');
    setPagination(prev => ({ ...prev, page: 1 })); // Reset página
    loadDocuments();
  }, searchQuery ? 300 : 0); // 300ms debounce solo para búsqueda

  return () => clearTimeout(timeoutId);
}, [selectedCategory, searchQuery, searchFilters]);

// 🔄 RECARGAR cuando cambia página (sin debounce)
useEffect(() => {
  if (pagination.page > 1) { // Solo si no es la primera página
    console.log('📄 Página cambiada - recargando documentos');
    loadDocuments();
  }
}, [pagination.page]);

// 🔄 RECARGAR cuando cambian filtros (con debounce para búsqueda)
useEffect(() => {
  const timeoutId = setTimeout(() => {
    if (selectedCategory !== null || searchQuery || Object.keys(searchFilters).length > 0) {
      setPagination(prev => ({ ...prev, page: 1 })); // Reset página
      loadDocuments();
    }
  }, searchQuery ? 300 : 0); // 300ms debounce solo para búsqueda

  return () => clearTimeout(timeoutId);
}, [selectedCategory, searchQuery, searchFilters]);

// 🔄 RECARGAR cuando cambia página (sin debounce)
useEffect(() => {
  if (pagination.page > 1) { // Solo si no es la primera página
    loadDocuments();
  }
}, [pagination.page]);


// ✅ FUNCIÓN loadCategories CON DEBUGGING
const loadCategories = async () => {
  try {
    console.log('📁 === LOAD CATEGORIES SIMPLE ===');
    
    const categoriesData = await knowledgeBaseService.getCategories();
    
    if (categoriesData && categoriesData.length > 0) {
      console.log('✅ Categorías cargadas:', categoriesData.length);
      setCategories(categoriesData);
    } else {
      console.warn('⚠️ Sin categorías del servidor');
      setCategories([]);
    }
    
    console.log('📁 === LOAD CATEGORIES SIMPLE FIN ===');
  } catch (error) {
    console.error('❌ Error cargando categorías:', error);
    setCategories([]);
  }
};

// ✅ FUNCIÓN loadDocuments CORREGIDA
// ✅ FUNCIÓN loadDocuments CON CONTROL AVANZADO
const loadDocuments = useCallback(async () => {
  // ✅ Prevenir múltiples llamadas simultáneas
  if (requestInProgress) {
    console.log('🚫 Request ya en progreso, cancelando...');
    return;
  }

  // ✅ Cancelar request anterior si existe
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }

  const currentRequestId = ++requestIdRef.current;
  const abortController = new AbortController();
  abortControllerRef.current = abortController;
  
  setRequestInProgress(true);
  setLoading(true);
  
  try {
    console.log(`📄 [${currentRequestId}] === CARGANDO DOCUMENTOS ===`);
    console.log('   - selectedCategory:', selectedCategory);
    
    const filters = {
      page: pagination.page,
      limit: pagination.limit,
      sort_by: 'created_at',
      sort_order: 'DESC' as 'DESC'
    };
    
    if (selectedCategory && selectedCategory !== 'null' && selectedCategory !== null) {
      filters.category_id = selectedCategory.toString();
      console.log('🔍 Aplicando filtro de categoría:', selectedCategory);
    }
        
    if (searchQuery && searchQuery.trim()) {
      filters.search = searchQuery.trim();
    }
    
    console.log('   - Filtros finales:', filters);
    
    const result = await knowledgeBaseService.getDocuments(filters, abortController.signal);
    
    // ✅ Solo actualizar si es el request más reciente y no fue cancelado
    if (currentRequestId === requestIdRef.current && !abortController.signal.aborted) {
      console.log(`✅ [${currentRequestId}] Documentos cargados:`, result.documents.length);
      
      setDocuments(result.documents);
      setPagination(prev => ({
        ...prev,
        ...result.pagination
      }));
    } else {
      console.log(`🚫 [${currentRequestId}] Request obsoleto, descartando...`);
    }
    
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log(`🚫 [${currentRequestId}] Request cancelado`);
      return;
    }
    
    // Solo mostrar error si es el request más reciente
    if (currentRequestId === requestIdRef.current) {
      console.error(`❌ [${currentRequestId}] Error cargando documentos:`, error);
      setDocuments([]);
    }
  } finally {
    // Solo actualizar loading si es el request más reciente
    if (currentRequestId === requestIdRef.current) {
      setLoading(false);
      setRequestInProgress(false);
    }
  }
}, [selectedCategory, searchQuery, searchFilters, pagination.page, pagination.limit]);


const syncCategoriesFromServer = async () => {
  try {
    console.log('🔄 Sincronizando categorías...');
    const serverCategories = await knowledgeBaseService.getCategories();
    
    if (serverCategories && serverCategories.length > 0) {
      setCategories(serverCategories);
      console.log('✅ Categorías sincronizadas:', serverCategories.length);
    }
  } catch (error) {
    console.warn('⚠️ Error sincronizando categorías:', error);
  }
};

  // 🔍 BÚSQUEDA
  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    loadDocuments();
  }, [searchQuery, searchFilters, selectedCategory]);

// AGREGAR después de loadDocuments (línea ~280):
const updateCategoryCount = useCallback(async () => {
  const updatedCategories = await knowledgeBaseService.getCategories();
  setCategories(updatedCategories);
}, []);

// Llamar después de subir/eliminar documentos
useEffect(() => {
  updateCategoryCount();
}, [documents.length]);

  
  // 📁 SELECCIÓN DE CATEGORÍA
const handleCategorySelect = (category: Category) => {
  console.log('🔍 === CATEGORY SELECT DEBUG ===');
  console.log('   - Category clickeada:', category);
  console.log('   - Category ID:', category.id);
  console.log('   - Category Name:', category.name);
  console.log('   - selectedCategory actual:', selectedCategory);
  
  // ✅ USAR ID DE CATEGORÍA CORRECTAMENTE
  const newSelection = category.id === selectedCategory ? null : category.id;
  
  console.log('   - Nueva selección:', newSelection);
  
  setSelectedCategory(newSelection);
  setPagination(prev => ({ ...prev, page: 1 }));
  
  console.log('🔍 === CATEGORY SELECT DEBUG FIN ===');
};

  // 📤 DRAG & DROP
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

// 📤 DRAG & DROP PRINCIPAL (CORREGIDO)
const handleDrop = useCallback((e: React.DragEvent) => {
  e.preventDefault();
  setDragOver(false);
  
  // ✅ VERIFICAR SI YA HAY UPLOAD EN PROGRESO
  if (isUploading || uploadInProgressRef.current) {
    console.log('🚫 Drag & Drop bloqueado - Upload en progreso');
    return;
  }
  
  const files = Array.from(e.dataTransfer.files);
  if (files.length > 0) {
    handleFileUpload(files);
  }
}, [isUploading]); // Agregar dependencia

// 📤 SUBIDA DE ARCHIVOS CON CONTROL TOTAL
const handleFileUpload = async (files: File[]) => {
  // ✅ MÚLTIPLES VERIFICACIONES DE BLOQUEO
  if (isUploading || uploadInProgressRef.current) {
    console.log('🚫 UPLOAD BLOQUEADO - Sistema ocupado');
    return;
  }

  if (!files || files.length === 0) {
    console.log('🚫 No hay archivos para subir');
    return;
  }

  console.log('🔒 INICIANDO UPLOAD PROTEGIDO');
  
  // ✅ TRIPLE BLOQUEO
  setIsUploading(true);
  uploadInProgressRef.current = true;
  setShowUploadModal(false);

  try {
    const file = files[0]; // Solo primer archivo
    console.log(`📤 Subiendo: ${file.name} (${file.size} bytes)`);
    
    await processFileUpload(file);
    
  } finally {
    console.log('🔓 LIBERANDO SISTEMA');
    setIsUploading(false);
    uploadInProgressRef.current = false;
  }
};

// ✅ NUEVA FUNCIÓN PARA PROCESAR CADA ARCHIVO
const processFileUpload = async (file: File) => {
  try {
    console.log('📤 === UPLOAD ÚNICO INICIADO ===');
    console.log('   - Archivo:', file.name, file.size);
    
    setUploadError('');
    setCurrentUploadFile(file.name);
    setCurrentUploadStep(0);
    setUploadProgressPercentage(0);
    setShowUploadProgress(true);

    // ✅ VERIFICACIÓN PREVIA DE DUPLICADOS EN FRONTEND
    const isDuplicate = documents.some(doc => 
      doc.file_name === file.name && 
      doc.file_size === file.size
    );
    
    if (isDuplicate) {
      throw new Error(`El archivo "${file.name}" ya existe`);
    }

    const formData = new FormData();
    formData.append('document', file);
    formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
    formData.append('description', '');
    formData.append('userId', user.id.toString());
    
    if (selectedCategory) {
      formData.append('category_id', selectedCategory);
    }

    console.log('📋 Enviando archivo único...');

    // ✅ SUBIDA CON TIMEOUT
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 segundos

    const response = await fetch('/api/knowledge-base/documents/upload', {
      method: 'POST',
      body: formData,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log('✅ Upload exitoso:', result);

    if (result.success) {
  setCurrentUploadStep(4);
  setUploadProgressPercentage(100);
  
  // ✅ INICIAR MONITOREO INMEDIATAMENTE
  const documentId = result.documentId || result.document?.id;
      if (documentId) {
        console.log('🔍 Iniciando monitoreo para documento:', documentId);
        monitorFileProcessing(documentId.toString(), file);
      } else {
        // Fallback si no hay ID
        setTimeout(async () => {
          setShowUploadProgress(false);
          await Promise.all([loadDocuments(), loadCategories()]);
          showToast(`✅ ${file.name} subido exitosamente`, 'success');
        }, 1500);
      }
    }

    console.log('📤 === UPLOAD ÚNICO FINALIZADO ===');

  } catch (error) {
    console.error('❌ Error en upload único:', error);
    setUploadError(error.message || 'Error desconocido');
    showToast(`❌ Error: ${error.message}`, 'error');
    
    setTimeout(() => {
      setShowUploadProgress(false);
    }, 3000);
  }
};

  // ✅ FUNCIÓN handleDelete CON TOAST
const handleDelete = async (documentId: string) => {
  const documentToDelete = documents.find(doc => doc.id === documentId);
  
  if (!documentToDelete) {
    showToast('❌ Documento no encontrado', 'error');
    return;
  }
  
  if (documentToDelete.user_id && user.id && documentToDelete.user_id.toString() !== user.id.toString()) {
    showToast('❌ No tienes permisos para eliminar este documento', 'error');
    return;
  }

  console.log('🗑️ === DELETE SIMPLE DEBUG ===');
  console.log('   - Documento a eliminar:', documentToDelete.title);
  console.log('   - Categoría:', documentToDelete.category_name);

  showConfirm(
    'Eliminar documento',
    `¿Estás seguro de que quieres eliminar "${documentToDelete.title}"?`,
    async () => {
      try {
        console.log('🗑️ Eliminando documento:', documentId);
        
        // ✅ ELIMINAR DEL SERVIDOR
        await knowledgeBaseService.deleteDocument(documentId);
        
        console.log('✅ Documento eliminado - actualizando interfaz...');
        
        // ✅ ACTUALIZAR CONTADORES EN BACKEND PRIMERO
        try {
          await fetch('http://localhost:3001/api/knowledge-base/categories/update-counts', {
            method: 'POST'
          });
          console.log('📊 Contadores actualizados en servidor');
        } catch (error) {
          console.warn('⚠️ Error actualizando contadores:', error);
        }
        
        // ✅ RECARGAR TODO PARA SINCRONIZAR
        await Promise.all([
          loadDocuments(),
          loadCategories()
        ]);
        
        showToast('📄 Documento eliminado exitosamente', 'success');
        
        console.log('🗑️ === DELETE SIMPLE DEBUG FIN ===');
        
      } catch (error) {
        console.error('❌ Error eliminando documento:', error);
        showToast(`❌ Error eliminando documento: ${error.message}`, 'error');
      }
    },
    'danger'
  );
};

  // 📊 MONITOREAR PROCESAMIENTO
const monitorFileProcessing = async (documentId: string, file: File) => {
  let attempts = 0;
  const maxAttempts = 30; // 30 intentos = 1 minuto
  
  const checkStatus = async () => {
    try {
      attempts++;
      console.log(`🔍 Verificando estado ${attempts}/${maxAttempts} para documento:`, documentId);
      
      const status = await knowledgeBaseService.getProcessingStatus(documentId);
      console.log('📊 Estado recibido:', status.processing_status);
      
      switch (status.processing_status) {
        case 'pending':
          setCurrentUploadStep(1);
          setUploadProgressPercentage(prev => Math.min(50, prev + 2));
          break;
          
        case 'processing':
          setCurrentUploadStep(2);
          setUploadProgressPercentage(prev => Math.min(85, prev + 2));
          break;
          
        case 'completed':
          console.log('✅ Documento completado, actualizando interfaz');
          setCurrentUploadStep(4);
          setUploadProgressPercentage(100);
          
          // ✅ ACTUALIZAR TODO INMEDIATAMENTE
          setTimeout(async () => {
            await Promise.all([
              loadDocuments(),
              loadCategories()
            ]);
            setShowUploadProgress(false);
            showToast(`✅ ${file.name} procesado completamente`, 'success');
          }, 1000);
          
          return; // ✅ SALIR DEL MONITOREO
          
        case 'error':
          console.error('❌ Error en procesamiento');
          setUploadError('Error durante el procesamiento del documento');
          showToast(`❌ Error procesando ${file.name}`, 'error');
          setTimeout(() => setShowUploadProgress(false), 3000);
          return; // ✅ SALIR DEL MONITOREO
      }
      
      // ✅ CONTINUAR MONITOREANDO SI NO ESTÁ COMPLETO
      if (attempts < maxAttempts) {
        setTimeout(checkStatus, 2000); // Verificar cada 2 segundos
      } else {
        console.warn('⚠️ Tiempo límite alcanzado, asumiendo completado');
        setTimeout(async () => {
          await loadDocuments();
          setShowUploadProgress(false);
          showToast(`⚠️ ${file.name} - verificar estado manualmente`, 'warning');
        }, 1000);
      }
      
    } catch (error) {
      console.error('❌ Error monitoreando procesamiento:', error);
      
      // ✅ FALLBACK: Asumir completado después de error
      setTimeout(async () => {
        await loadDocuments();
        setShowUploadProgress(false);
        showToast(`⚠️ ${file.name} subido - verificar estado`, 'warning');
      }, 1000);
    }
  };

  // ✅ INICIAR VERIFICACIÓN
  setTimeout(checkStatus, 1000); // Empezar después de 1 segundo
};
  // 👁️ PREVIEW DE DOCUMENTO
  const handlePreview = (document: Document) => {
    setSelectedDocument(document);
    setShowPreview(true);
  };

  // ⬇️ DESCARGAR DOCUMENTO
const handleDownload = async (documentId: string) => {
  try {
    console.log('⬇️ Descargando documento:', documentId);
    await knowledgeBaseService.downloadDocument(documentId);
  } catch (error) {
    console.error('❌ Error descargando documento:', error);
  }
};

// 🎨 OBTENER ICONO DE ARCHIVO (CORREGIDO)
const getFileIcon = (fileType: string | undefined) => {
  const iconClass = "w-5 h-5";
  
  // ✅ VALIDACIÓN PARA EVITAR ERRORES
  if (!fileType) {
    return <FileText className={`${iconClass} text-gray-500`} />;
  }
  
  // ✅ NORMALIZAR fileType
  const normalizedType = fileType.toLowerCase();
  
  switch (normalizedType) {
    case '.pdf':
    case 'pdf': 
      return <FileText className={`${iconClass} text-red-500`} />;
    case '.doc':
    case '.docx':
    case 'doc':
    case 'docx': 
      return <FileText className={`${iconClass} text-blue-500`} />;
    case '.xls':
    case '.xlsx':
    case 'xls':
    case 'xlsx': 
      return <FileText className={`${iconClass} text-green-500`} />;
    case '.jpg':
    case '.jpeg':
    case '.png':
    case 'jpg':
    case 'jpeg':
    case 'png': 
      return <FileText className={`${iconClass} text-purple-500`} />;
    case '.txt':
    case 'txt':
      return <FileText className={`${iconClass} text-yellow-500`} />;
    default: 
      return <FileText className={`${iconClass} text-gray-500`} />;
  }
};

  // 🎨 OBTENER ICONO DE ESTADO
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'processing': return <Loader className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'error': return <AlertCircle className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  return (
    <div 
      className="space-y-8"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* 🔍 HEADER DE BÚSQUEDA */}
      <div className={`${isDark ? 'bg-gray-800/50' : 'bg-white/50'} backdrop-blur-sm rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-white/30'} shadow-lg`}>
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
              <input
                type="text"
                placeholder="Buscar documentos, manuales, procedimientos..."
                className={`block w-full pl-12 pr-4 py-3 border rounded-xl leading-5 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 ${
                  isDark 
                    ? 'bg-gray-700/50 border-gray-600 text-white' 
                    : 'bg-white/70 border-gray-300 text-gray-900'
                }`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
            >
              <Search className="w-5 h-5" />
              Buscar
            </button>
          <button
              type="button"
              onClick={() => setShowUploadModal(true)}
              disabled={isUploading} // ✅ DESHABILITAR DURANTE UPLOAD
              className={`px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 ${
                isUploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Upload className="w-5 h-5" />
              {isUploading ? 'Subiendo...' : 'Subir'}
            </button>
          </div>
          
          {/* FILTROS AVANZADOS */}
          <div className="flex flex-wrap gap-4">
            <select 
              className={`px-3 py-2 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              value={searchFilters.file_type || ''}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, file_type: e.target.value || undefined }))}
            >
              <option value="">Todos los tipos</option>
              <option value=".pdf">PDF</option>
              <option value=".doc,.docx">Word</option>
              <option value=".xls,.xlsx">Excel</option>
              <option value=".jpg,.png">Imágenes</option>
            </select>
          </div>
        </form>
      </div>

      {/* 📤 INDICADOR DE DRAG & DROP */}
      {dragOver && (
        <div className="fixed inset-0 bg-blue-500/20 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-8 shadow-2xl border-2 border-dashed border-blue-500`}>
            <div className="text-center">
              <Upload className="w-16 h-16 text-blue-500 mx-auto mb-4" />
              <p className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                Suelta los archivos aquí
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                PDF, Word, Excel, Imágenes (máx. 50MB)
              </p>
            </div>
          </div>
        </div>
      )}

      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* 📁 CATEGORÍAS */}
        <div className="lg:col-span-1">
          <h3 className={`font-semibold text-lg mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            Categorías
          </h3>
         <div className="space-y-3">
                {/* ✅ BOTÓN TODOS */}
                <div 
                  className={`group cursor-pointer transition-all duration-200 hover:scale-105 ${
                    selectedCategory === null
                      ? `${isDark ? 'bg-blue-900/30 border-blue-500' : 'bg-blue-50 border-blue-300'} shadow-lg` 
                      : `${isDark ? 'bg-gray-800/50 border-gray-700/50 hover:bg-gray-700/50' : 'bg-white/50 border-white/50 hover:bg-white/80'}`
                  } backdrop-blur-sm border rounded-xl p-4 shadow-md hover:shadow-lg`}
                  onClick={() => {
                    console.log('🔍 Click en TODOS - limpiando selectedCategory');
                    setSelectedCategory(null);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg bg-blue-100`}>
                      <FolderOpen className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        📁 Todos
                        {selectedCategory === null && " ✓"}
                      </h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Todas las categorías
                      </p>
                    </div>
                  </div>
                </div>
            {/* Categorías existentes */}    
            {categories.map(category => (
          <div 
            key={category.id}
            className={`group cursor-pointer transition-all duration-200 hover:scale-105 ${
              selectedCategory === category.id  // ✅ COMPARAR CON category.id
                ? `${isDark ? 'bg-purple-900/30 border-purple-500' : 'bg-purple-50 border-purple-300'} shadow-lg` 
                : `${isDark ? 'bg-gray-800/50 border-gray-700/50 hover:bg-gray-700/50' : 'bg-white/50 border-white/50 hover:bg-white/80'}`
            } backdrop-blur-sm border rounded-xl p-4 shadow-md hover:shadow-lg`}
            onClick={() => handleCategorySelect(category)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div 
                  className={`p-2 rounded-lg`}
                  style={{ backgroundColor: `${category.color}20` }}
                >
                  <FolderOpen 
                    className="w-5 h-5"
                    style={{ color: category.color }}
                  />
                </div>
                <div>
                  <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    {category.name}
                    {selectedCategory === category.id && " ✓"} {/* ✅ INDICADOR VISUAL */}
                  </h4>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {category.document_count} docs
                  </p>
                </div>
              </div>
            </div>
            <p className={`text-sm mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {category.description}
            </p>
          </div>
        ))}
          </div>
        </div>
        
        {/* 📄 DOCUMENTOS */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Documentos
            </h3>
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                <FileText className="w-4 h-4" />
                <span className="font-medium text-sm">
                  {pagination.total} documento{pagination.total !== 1 ? 's' : ''}
                </span>
              </div>
              {selectedCategory && (
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'}`}>
                  <Filter className="w-4 h-4" />
                  <span className="font-medium text-sm">Filtrados</span>
                </div>
              )}
            </div>
          </div>
             {selectedCategory && (
                <div className={`mb-6 p-4 rounded-xl ${isDark ? 'bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/30' : 'bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200'} backdrop-blur-sm`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                        <Filter className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                          Filtrado por categoría
                        </p>
                        <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                          {categories.find(c => c.id === selectedCategory)?.name}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setSelectedCategory(null)}
                      className={`p-2 rounded-lg transition-all hover:scale-105 ${isDark ? 'bg-gray-700/50 hover:bg-gray-600 text-gray-300' : 'bg-white/80 hover:bg-white text-gray-600'}`}
                      title="Limpiar filtro"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
              
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : documents.length === 0 ? (
            <div className={`${isDark ? 'bg-gray-800/50 border-gray-700/50' : 'bg-white/50 border-white/50'} backdrop-blur-sm border rounded-2xl p-12 text-center shadow-lg`}>
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                <FileText className={`w-8 h-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} text-lg`}>
                {selectedCategory 
                  ? 'No hay documentos disponibles en esta categoría.' 
                  : 'Selecciona una categoría o realiza una búsqueda para ver documentos.'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid gap-4">
                {documents.map(doc => (
                  <div key={doc.id} className={`group ${isDark ? 'bg-gray-800/50 border-gray-700/50 hover:bg-gray-700/50' : 'bg-white/50 border-white/50 hover:bg-white/80'} backdrop-blur-sm border rounded-xl p-6 hover:shadow-lg transition-all duration-200 hover:scale-[1.02]`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-grow">
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                          {getFileIcon(doc.file_type)}
                        </div>
                        <div className="flex-grow">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-800'} group-hover:text-blue-600 transition-colors`}>
                              {doc.title}
                            </h4>
                            {getStatusIcon(doc.processing_status)}
                          </div>
                          <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mt-1`}>
                            {doc.description}
                          </p>
                          <div className="flex items-center gap-4 mt-3 flex-wrap">
                            <span 
                              className={`text-xs px-3 py-1 rounded-full border`}
                              style={{ 
                                backgroundColor: `${doc.category_color}20`,
                                borderColor: `${doc.category_color}40`,
                                color: doc.category_color
                              }}
                            >
                              {doc.category_name}
                            </span>
                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} flex items-center gap-1`}>
                              <Calendar className="w-3 h-3" />
                              {new Date(doc.created_at).toLocaleDateString()}
                            </span>
                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {doc.file_size_formatted}
                            </span>
                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {doc.view_count} vistas
                            </span>
                            {doc.technical_keywords && doc.technical_keywords.length > 0 && (
                              <div className="flex gap-2 flex-wrap">
                                {doc.technical_keywords.slice(0, 2).map((keyword, idx) => (
                                  <span 
                                    key={idx} 
                                    className={`text-xs px-3 py-1 rounded-full border font-medium ${
                                      isDark 
                                        ? 'bg-purple-900/30 border-purple-500/30 text-purple-300' 
                                        : 'bg-purple-50 border-purple-200 text-purple-700'
                                    } hover:scale-105 transition-transform`}
                                  >
                                    #{keyword}
                                  </span>
                                ))}
                                {doc.technical_keywords.length > 2 && (
                                  <span className={`text-xs px-2 py-1 rounded-full ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    +{doc.technical_keywords.length - 2}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button 
                          onClick={() => handlePreview(doc)}
                          className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 ${isDark ? 'hover:bg-gray-600/50 text-gray-400 hover:text-blue-400' : 'hover:bg-blue-50 text-gray-600 hover:text-blue-600'}`} 
                          title="Ver documento"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={() => handleDownload(doc.id)}
                          className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 ${isDark ? 'hover:bg-gray-600/50 text-gray-400 hover:text-green-400' : 'hover:bg-green-50 text-gray-600 hover:text-green-600'}`} 
                          title="Descargar"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                         {/* ✅ BOTÓN ELIMINAR - MEJORAR LÓGICA DE PERMISOS */}
                          {(() => {
                            const hasPermission = !doc.user_id || 
                                                 !user.id || 
                                                 doc.user_id.toString() === user.id.toString();
                            
                            return hasPermission ? (
                              <button 
                                onClick={() => handleDelete(doc.id)}
                                className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 ${
                                  isDark ? 'hover:bg-red-600/20 text-gray-400 hover:text-red-400' : 'hover:bg-red-50 text-gray-600 hover:text-red-600'
                                }`} 
                                title="Eliminar documento"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            ) : null;
                          })()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* PAGINACIÓN */}
              {pagination.totalPages > 1 && (
                <div className="flex justify-center mt-8">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      className={`px-4 py-2 rounded-lg ${pagination.page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'} ${isDark ? 'text-white hover:bg-gray-700' : 'text-gray-700'}`}
                    >
                      Anterior
                    </button>
                    <span className={`px-4 py-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                      {pagination.page} de {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                      disabled={pagination.page === pagination.totalPages}
                      className={`px-4 py-2 rounded-lg ${pagination.page === pagination.totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'} ${isDark ? 'text-white hover:bg-gray-700' : 'text-gray-700'}`}
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 🔍 MODAL DE PREVIEW */}
      {showPreview && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden`}>
            <div className={`p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {selectedDocument.title}
                </h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className={`p-2 rounded-lg hover:bg-gray-100 ${isDark ? 'hover:bg-gray-700 text-white' : 'text-gray-600'}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-96">
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
                {selectedDocument.description}
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Tipo:</span>
                  <span className={`ml-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {selectedDocument.file_type.toUpperCase()}
                  </span>
                </div>
                <div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Tamaño:</span>
                  <span className={`ml-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {selectedDocument.file_size_formatted}
                  </span>
                </div>
                <div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Vistas:</span>
                  <span className={`ml-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {selectedDocument.view_count}
                  </span>
                </div>
                <div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Confianza:</span>
                  <span className={`ml-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {Math.round(selectedDocument.confidence_score * 100)}%
                  </span>
                </div>
              </div>
            </div>
            <div className={`p-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-end gap-3`}>
              <button
                onClick={() => handleDownload(selectedDocument.id)}
                className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Descargar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 📤 MODAL DE SUBIDA DE ARCHIVOS */}
{showUploadModal && (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
    <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden`}>
      <div className={`p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
            Subir Documentos
          </h3>
          <button
            onClick={() => setShowUploadModal(false)}
            className={`p-2 rounded-lg hover:bg-gray-100 ${isDark ? 'hover:bg-gray-700 text-white' : 'text-gray-600'}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      <div className="p-6">
        {/* Área de drag & drop */}
        <div 
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
            isDark 
              ? 'border-gray-600 bg-gray-700/50 hover:border-blue-500 hover:bg-blue-500/10' 
              : 'border-gray-300 bg-gray-50 hover:border-blue-500 hover:bg-blue-50'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) {
              handleFileUpload(files);
              setShowUploadModal(false);
            }
          }}
        >
          <Upload className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          <h4 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            Arrastra archivos aquí
          </h4>
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            O haz click para seleccionar archivos
          </p>
          
          {/* Input de archivo oculto */}
         <input
            type="file"
            multiple={false} // ✅ SOLO UN ARCHIVO
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0 && !isUploading) { // ✅ VERIFICAR isUploading
                handleFileUpload(Array.from(e.target.files));
                setShowUploadModal(false);
              }
            }}
            disabled={isUploading} // ✅ DESHABILITAR DURANTE UPLOAD
            className="hidden"
            id="file-upload"
          />
          
          <label
            htmlFor="file-upload"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 cursor-pointer"
          >
            <Upload className="w-5 h-5" />
            Seleccionar Archivos
          </label>
          
          <div className={`mt-4 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <p>Tipos permitidos: PDF, Word, Excel, Imágenes, Texto</p>
            <p>Tamaño máximo: 50MB por archivo</p>
          </div>
        </div>
        
        {/* Formulario adicional */}
        <div className="mt-6 space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
              Categoría
            </label>
            <select 
              className={`w-full px-3 py-2 rounded-lg border ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value || null)}
            >
              <option value="">Seleccionar categoría...</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      <div className={`p-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-end gap-3`}>
        <button
          onClick={() => setShowUploadModal(false)}
          className={`px-6 py-2 rounded-lg border transition-colors ${
            isDark 
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          Cancelar
        </button>
      </div>
    </div>
  </div>
)}

<UploadProgress
  isVisible={showUploadProgress}
  currentStep={currentUploadStep}
  progress={uploadProgressPercentage}
  fileName={currentUploadFile}
  onClose={() => setShowUploadProgress(false)}
  error={uploadError}
/>

 {/* ✅ TOAST */}
    <Toast
      message={toastMessage}
      type={toastType}
      isVisible={toastVisible}
      onClose={() => setToastVisible(false)}
    />
    
    {/* ✅ MODAL DE CONFIRMACIÓN */}
    <ConfirmModal
      isOpen={confirmModal.isOpen}
      title={confirmModal.title}
      message={confirmModal.message}
      onConfirm={confirmModal.onConfirm}
      onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      type={confirmModal.type}
      confirmText="Eliminar"
      cancelText="Cancelar"
    />
    </div>
  );
};

export default KnowledgeBase;